import type { ExcelExchangeManifest, ExcelImportJobRow, ExcelImportMode } from '@erpgold/shared';
import type { ExportFormat, ExportJob } from './export.service';
import api from './api';

export type ExcelImportEntity = 'products' | 'customers' | 'suppliers';
export type ExcelExportEntity = ExcelImportEntity;

export interface ExcelImportUploadSheet {
  index: number;
  name: string;
  row_count: number;
}

export type ExcelPreviewCell = string | number | boolean | null;
export type ExcelPreviewRow = ExcelPreviewCell[];

export interface ExcelImportUploadResponse {
  upload_id: string;
  columns: string[];
  preview: ExcelPreviewRow[];
  preview_rows: ExcelPreviewRow[];
  total_rows: number;
  file_name: string;
  file_size: number;
  parser_type: string;
  sheets: ExcelImportUploadSheet[];
  sheet_index: number;
  header_row: number;
}

export interface ExcelAnalyzeImportPayload {
  upload_id: string;
  sheet_index?: number;
  header_row?: number;
}

export interface ExcelAnalyzeImportResponse {
  upload_id: string;
  columns: string[];
  preview_rows: ExcelPreviewRow[];
  total_rows: number;
  sheet_index: number;
  header_row: number;
  file_name: string;
  file_size: number;
  parser_type: string;
  sheets: ExcelImportUploadSheet[];
}

export interface ExcelValidateImportPayload extends ExcelAnalyzeImportPayload {
  entity: ExcelImportEntity;
  mapping?: Record<string, string>;
  mode?: ExcelImportMode;
}

export interface CreateExcelExportPayload {
  entity: ExcelExportEntity;
  format: ExportFormat;
  date_range?: {
    from?: string;
    to?: string;
  };
  scope?: 'current_filter' | 'all_allowed';
  column_profile_key?: string;
  field_keys?: string[];
  test?: boolean;
}

export interface ExcelImportJobStatus {
  import_id: string;
  tenant_id: string;
  upload_id: string | null;
  entity: ExcelImportEntity;
  file_name: string;
  duplicate_action: 'skip' | 'upsert' | 'create';
  mapping: Record<string, string>;
  status: 'pending' | 'processing' | 'awaiting_confirmation' | 'completed' | 'failed';
  progress: number;
  total_rows: number;
  processed_rows: number;
  success_rows: number;
  warning_rows: number;
  error_rows: number;
  parser_type: string | null;
  memory_peak_mb: number;
  error: string | null;
  created_by: string | null;
  is_dryrun: boolean;
  action_summary: {
    create_rows: number;
    update_rows: number;
    skip_rows: number;
    warning_rows: number;
    error_rows: number;
  } | null;
  created_at?: string;
  started_at: string | null;
  completed_at: string | null;
  awaiting_confirmation_at: string | null;
  confirmed_at: string | null;
  validation_result: Record<string, unknown> | null;
}

export interface ExcelImportJobRowsQuery {
  page?: number;
  page_size?: number;
  status?: 'all' | 'valid' | 'warning' | 'error';
}

export interface ExcelImportJobRowItem extends ExcelImportJobRow {
  issues: Array<{
    row: number;
    column: string;
    message: string;
    severity: 'error' | 'warning';
  }>;
  source_data: Record<string, unknown>;
}

export interface ExcelImportJobRowsResponse {
  data: ExcelImportJobRowItem[];
  total: number;
  page: number;
  page_size: number;
  summary: {
    valid: number;
    warning: number;
    error: number;
  };
}

export const excelService = {
  async getManifest(entity: string): Promise<ExcelExchangeManifest> {
    const response = await api.get<ExcelExchangeManifest>(`/excel/manifests/${entity}`);
    return response.data;
  },

  async uploadImportFile(file: File): Promise<ExcelImportUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<ExcelImportUploadResponse>('/excel/import/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async analyzeImport(payload: ExcelAnalyzeImportPayload): Promise<ExcelAnalyzeImportResponse> {
    const response = await api.post<ExcelAnalyzeImportResponse>('/excel/import/analyze', payload);
    return response.data;
  },

  async validateImport(payload: ExcelValidateImportPayload): Promise<ExcelImportJobStatus> {
    const response = await api.post<ExcelImportJobStatus>('/excel/import/validate', payload);
    return response.data;
  },

  async getImportRows(id: string, params: ExcelImportJobRowsQuery = {}): Promise<ExcelImportJobRowsResponse> {
    const response = await api.get<ExcelImportJobRowsResponse>(`/excel/import-jobs/${id}/rows`, { params });
    return response.data;
  },

  async commitImport(id: string): Promise<ExcelImportJobStatus> {
    const response = await api.post<ExcelImportJobStatus>(`/excel/import-jobs/${id}/commit`);
    return response.data;
  },

  async downloadImportErrorFile(id: string): Promise<Blob> {
    const response = await api.get(`/excel/import-jobs/${id}/error-file`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  async createExportJob(payload: CreateExcelExportPayload): Promise<ExportJob> {
    const response = await api.post<ExportJob>('/excel/export-jobs', payload);
    return response.data;
  },

  async getExportJob(id: string): Promise<ExportJob> {
    const response = await api.get<ExportJob>(`/excel/export-jobs/${id}`);
    return response.data;
  },
};

export type { ExcelExchangeManifest };
